import { Device, Call } from "@twilio/voice-sdk";
import type { TwilioCallEventListener, TwilioVoiceToken } from "./types";

// Web implementation using @twilio/voice-sdk
let deviceInstance: Device | null = null;
let currentCall: Call | null = null;
let isIncomingCall = false;
let eventListeners: TwilioCallEventListener[] = [];
let tokenRefresher: (() => Promise<TwilioVoiceToken>) | null = null;
let recovering = false;

export function setTokenRefresher(refresher: (() => Promise<TwilioVoiceToken>) | null): void {
  tokenRefresher = refresher;
}

/**
 * Attempt to re-register the device after a WebSocket drop.
 * If registration fails (likely token expired), fetches a fresh token and retries.
 * Silently gives up on failure — next user action will re-initialize.
 */
async function recoverDevice(): Promise<void> {
  if (recovering || !deviceInstance) return;
  recovering = true;
  try {
    await deviceInstance.register();
  } catch {
    // Registration failed — try refreshing the token
    if (tokenRefresher) {
      try {
        const newToken = await tokenRefresher();
        deviceInstance!.updateToken(newToken.token);
        await deviceInstance!.register();
      } catch (refreshErr) {
        console.warn("[TwilioVoice Web] Recovery failed, will retry on next action:", refreshErr);
      }
    }
  } finally {
    recovering = false;
  }
}

/**
 * Initialize Twilio Voice SDK for web with access token
 */
export async function initializeTwilioVoice(token: TwilioVoiceToken): Promise<void> {
  try {
    if (!deviceInstance) {
      deviceInstance = new Device(token.token, {
        codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
      });

      // Proactively refresh token before it expires
      deviceInstance.on("tokenWillExpire", async () => {
        if (!tokenRefresher || !deviceInstance) return;
        try {
          const newToken = await tokenRefresher();
          deviceInstance.updateToken(newToken.token);
        } catch (err) {
          console.warn("[TwilioVoice Web] Token refresh failed:", err);
        }
      });

      // Device registered successfully
      deviceInstance.on("registered", () => {});

      deviceInstance.on("unregistered", () => {});

      // Handle incoming calls
      deviceInstance.on("incoming", (call) => {
        console.log("[TwilioVoice Web] Incoming call from:", call.parameters?.From);
        currentCall = call;
        isIncomingCall = true;
        setupCallListeners(call);
        notifyListeners({
          type: "incoming",
          call,
          from: call.parameters?.From,
          to: call.parameters?.To,
          callSid: call.parameters?.CallSid,
        });
      });

      deviceInstance.on("error", (error) => {
        console.warn("[TwilioVoice Web] Device error:", error);
        recoverDevice();
      });
    }

    // Register device
    await deviceInstance.register();
  } catch (error) {
    throw new Error(
      "[TwilioVoice Web] Failed to initialize: " + (error instanceof Error ? error.message : String(error)),
    );
  }
}

/**
 * Make an outbound call
 */
export async function makeCall(phoneNumber: string, params?: Record<string, string>): Promise<Call> {
  if (!deviceInstance) {
    throw new Error("Twilio Voice not initialized");
  }

  try {
    // Connect using device.connect with params
    currentCall = await deviceInstance.connect({
      params: {
        To: phoneNumber,
        ...params,
      },
    });

    setupCallListeners(currentCall);
    notifyListeners({ type: "connecting", call: currentCall });

    return currentCall;
  } catch (error: any) {
    throw new Error(
      "[TwilioVoice Web] Failed to make call: " + (error instanceof Error ? error.message : String(error)),
    );
  }
}

/**
 * Set up event listeners for a call
 */
function setupCallListeners(call: Call): void {
  call.on("accept", () => {
    isIncomingCall = false;
    notifyListeners({ type: "connected", call });
  });

  call.on("ringing", () => {
    notifyListeners({ type: "ringing", call });
  });

  call.on("disconnect", () => {
    isIncomingCall = false;
    notifyListeners({ type: "disconnected", call });
    currentCall = null;
  });

  call.on("cancel", () => {
    if (isIncomingCall) {
      // Incoming call was cancelled — another user likely answered
      console.log("[TwilioVoice Web] Incoming call cancelled (answered elsewhere)");
      notifyListeners({
        type: "answered_elsewhere",
        call,
        callSid: call.parameters?.CallSid,
      });
    } else {
      notifyListeners({ type: "disconnected", call });
    }
    isIncomingCall = false;
    currentCall = null;
  });

  call.on("reject", () => {
    notifyListeners({ type: "failed", call });
    currentCall = null;
  });

  call.on("error", (error) => {
    notifyListeners({ type: "failed", call, error: error?.message });
    currentCall = null;
  });
}

/**
 * Accept an incoming call
 */
export async function acceptCall(): Promise<void> {
  if (currentCall) {
    currentCall.accept();
  }
}

/**
 * Reject an incoming call
 */
export async function rejectCall(): Promise<void> {
  if (currentCall) {
    currentCall.reject();
    currentCall = null;
  }
}

/**
 * End the current call
 */
export async function endCall(): Promise<void> {
  if (currentCall) {
    currentCall.disconnect();
    currentCall = null;
  }
}

/**
 * Mute/unmute the current call
 */
export async function setMuted(muted: boolean): Promise<void> {
  if (currentCall) {
    currentCall.mute(muted);
  }
}

/**
 * Get current call status
 */
export function getCallStatus(): string | null {
  return currentCall ? currentCall.status() : null;
}

/**
 * Check if there's an active call
 */
export function hasActiveCall(): boolean {
  return currentCall !== null;
}

/**
 * Add event listener for call state changes
 */
export function addEventListener(listener: TwilioCallEventListener): void {
  eventListeners.push(listener);
}

/**
 * Remove event listener
 */
export function removeEventListener(listener: TwilioCallEventListener): void {
  eventListeners = eventListeners.filter((l) => l !== listener);
}

/**
 * Unregister device and cleanup
 */
export async function cleanup(): Promise<void> {
  if (currentCall) {
    currentCall.disconnect();
    currentCall = null;
  }

  if (deviceInstance) {
    deviceInstance.unregister();
    deviceInstance.destroy();
    deviceInstance = null;
  }

  eventListeners = [];
  tokenRefresher = null;
  recovering = false;
}

/**
 * Notify all listeners of a call event
 */
function notifyListeners(event: Parameters<TwilioCallEventListener>[0]): void {
  eventListeners.forEach((listener) => {
    try {
      listener(event);
    } catch (error) {
      console.error("[TwilioVoice Web] Listener error:", error);
    }
  });
}
